/**
 * The account menu's entries — ONE list, read by today's dropdown (src/components/ProfileImage.tsx) and the new
 * design's header (its avatar menu and the phone's sheet), so the two can never offer different places or disagree
 * about who may see a billing page.
 *
 * The rules, each one the menu has always had:
 * - the page you are on is not offered;
 * - wallet and subscription follow the billing.view gate (`canSeeWallet`, fail-open until the permission resolves),
 *   activity the activity.view gate (`canSeeActivity`) — a member an admin restricted gets no path to the access wall
 *   (ORG-USAGE-2026, task #77); a direct address still lands on the honest wall;
 * - activity sits between wallet and profile; team & roles matches its sub-pages, integrations its sub-pages;
 * - skills (the procedures Prest0n follows here, skills PR J) sits after team & roles: every member may open it,
 *   and the page itself shows each person only the sections their role allows;
 * - the insights studio (insights studio S2, 2026-10-16) sits after skills, offered only in Prest0 Inc.'s workspace to
 *   someone holding insights.posts.write (`canSeeInsightsStudio`, src/hooks/useInsightsStudioAccess.ts, from the same
 *   useUsageVisibility answer); absent means not offered. It hides chrome only: the route's DeveloperRoute and every
 *   callable decide;
 * - the roadmap studio (the roadmap and changelog R3, 2026-10-20) sits after the insights studio, offered on the same
 *   terms for `roadmap.items.view` (`canSeeRoadmapStudio`, src/hooks/useRoadmapStudioAccess.ts);
 * - the engineering page (2026-10-23, /activity/engineering: automatic development, the Claude Code queue and the rest
 *   of Prest0 Inc.'s engineering views) sits after the roadmap studio, offered on the same terms for
 *   `developer.escalations.view` (`canSeeEngineering`, src/hooks/useEngineeringAccess.ts);
 * - the sales funnel (2026-11-03, /activity/sales-funnel: new people and organizations across the platform, their trials,
 *   stages and conversion; docs/PLAN-sales-funnel-20261103.md) sits after engineering, offered on the same terms for
 *   `growth.sales-funnel.view`, which only Prest0 Inc.'s owner and admins hold (`canSeeSalesFunnel`,
 *   src/hooks/useSalesFunnelAccess.ts);
 * - the referral program (2026-10-19, docs/PLAN-referral-program-20261019.md) sits after the wallet, where its rewards
 *   land, for everyone: every person may open their own referral page, and it says whether they may join from the
 *   workspace they are in;
 * - what's new (2026-10-21, prest0.ai/changelog: every update to prest0, month by month) comes last, for everyone;
 * - no entry changes the light: the account menu carries no theme control (D-66,
 *   tests/landing/accountMenuHasNoThemeRow.test.tsx).
 *
 * Logout is not an entry: it is an action, and each surface draws it last, apart from the places.
 */
import { useLocation } from 'react-router-dom';
import { useUsageVisibility } from './useUsageVisibility';
import { canSeeInsightsStudio, useActiveWorkspaceAccountId } from './useInsightsStudioAccess';
import { canSeeRoadmapStudio } from './useRoadmapStudioAccess';
import { canSeeEngineering } from './useEngineeringAccess';
import { canSeeSalesFunnel } from './useSalesFunnelAccess';
import { personasPageShows } from '../services/personas/personasLever';
import { profilesShow } from '../services/profiles/profilesLever';

export type AccountMenuId =
  | 'desktop'
  | 'wallet'
  | 'referrals'
  | 'activity'
  | 'people'
  | 'profile'
  | 'settings'
  | 'team'
  | 'skills'
  | 'personas'
  | 'insights-studio'
  | 'roadmap-studio'
  | 'engineering'
  | 'sales-funnel'
  | 'organization'
  | 'integrations'
  | 'subscription'
  | 'whats-new';

export interface AccountMenuEntry {
  id: AccountMenuId;
  label: string;
  to: string;
}

export interface AccountMenuFacts {
  pathname: string;
  canSeeWallet: boolean;
  canSeeActivity: boolean;
  /** Prest0 Inc.'s workspace and insights.posts.write; absent means the studio is not offered. */
  canSeeInsightsStudio?: boolean;
  /** Prest0 Inc.'s workspace and roadmap.items.view; absent means the studio is not offered. */
  canSeeRoadmapStudio?: boolean;
  /** Prest0 Inc.'s workspace and developer.escalations.view; absent means the engineering page is not offered. */
  canSeeEngineering?: boolean;
  /** Prest0 Inc.'s workspace and growth.sales-funnel.view; absent means the sales funnel is not offered. */
  canSeeSalesFunnel?: boolean;
  /** The Personas page's client lever admits this workspace (personasLever.ts); absent means it is not offered. */
  canSeePersonas?: boolean;
  /** The profiles' client lever admits this workspace (profilesLever.ts): the People page; absent means it is not offered. */
  canSeePeople?: boolean;
}

/** The entries for these facts, in the menu's order. Pure. */
export function accountMenuEntries({
  pathname, canSeeWallet, canSeeActivity, canSeeInsightsStudio = false, canSeeRoadmapStudio = false, canSeeEngineering = false, canSeeSalesFunnel = false,
  canSeePersonas = false, canSeePeople = false,
}: AccountMenuFacts): AccountMenuEntry[] {
  const here = (to: string) => pathname === to;
  const under = (to: string) => pathname.startsWith(to);
  const out: AccountMenuEntry[] = [];
  if (!here('/desktop')) out.push({ id: 'desktop', label: 'desktop', to: '/desktop' });
  if (canSeeWallet && !here('/wallet')) out.push({ id: 'wallet', label: 'wallet', to: '/wallet' });
  if (!here('/referrals/dashboard')) out.push({ id: 'referrals', label: 'refer & earn', to: '/referrals/dashboard' });
  if (canSeeActivity && !here('/activity')) out.push({ id: 'activity', label: 'activity', to: '/activity' });
  if (canSeePeople && !here('/people')) out.push({ id: 'people', label: 'people', to: '/people' });
  if (!here('/profile')) out.push({ id: 'profile', label: 'profile', to: '/profile' });
  if (!here('/settings')) out.push({ id: 'settings', label: 'settings', to: '/settings' });
  if (!under('/settings/team')) out.push({ id: 'team', label: 'team & roles', to: '/settings/team' });
  if (!here('/settings/skills')) out.push({ id: 'skills', label: 'skills', to: '/settings/skills' });
  if (canSeePersonas && !here('/settings/personas')) out.push({ id: 'personas', label: 'personas', to: '/settings/personas' });
  if (canSeeInsightsStudio && !under('/admin/insights')) out.push({ id: 'insights-studio', label: 'insights studio', to: '/admin/insights' });
  if (canSeeRoadmapStudio && !under('/admin/roadmap')) out.push({ id: 'roadmap-studio', label: 'roadmap studio', to: '/admin/roadmap' });
  if (canSeeEngineering && !under('/activity/engineering')) out.push({ id: 'engineering', label: 'engineering', to: '/activity/engineering' });
  if (canSeeSalesFunnel && !under('/activity/sales-funnel')) out.push({ id: 'sales-funnel', label: 'sales funnel', to: '/activity/sales-funnel' });
  if (!here('/organization')) out.push({ id: 'organization', label: 'organization', to: '/organization' });
  if (!under('/integrations')) out.push({ id: 'integrations', label: 'integrations', to: '/integrations' });
  if (canSeeWallet && !here('/subscription')) out.push({ id: 'subscription', label: 'subscription', to: '/subscription' });
  if (!under('/changelog')) out.push({ id: 'whats-new', label: "what's new", to: '/changelog' });
  return out;
}

/** The entries for the signed-in person on the page they are on. */
export function useAccountMenuItems(): AccountMenuEntry[] {
  const { pathname } = useLocation();
  const visibility = useUsageVisibility();
  const accountId = useActiveWorkspaceAccountId();
  return accountMenuEntries({
    pathname,
    canSeeWallet: visibility.canSeeWallet,
    canSeeActivity: visibility.canSeeActivity,
    canSeeInsightsStudio: canSeeInsightsStudio(accountId, visibility),
    canSeeRoadmapStudio: canSeeRoadmapStudio(accountId, visibility),
    canSeeEngineering: canSeeEngineering(accountId, visibility),
    canSeeSalesFunnel: canSeeSalesFunnel(accountId, visibility),
    canSeePersonas: personasPageShows(accountId),
    canSeePeople: profilesShow(accountId),
  });
}
